import React, { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { renderRoutes, matchRoutes } from "react-router-config";
import { connect } from "react-redux";
import { parse } from "query-string";
import { mapProperties, hydrateRoute } from "colbi_web_ui/lib/utils";
import { applyThemeWithPriority, hasModule } from "../../utils/utils";
import Header from "./Header/Header";
import Connect from "colbi_web_ui/lib/components/Connect";
import {
    asideRepositoryNavigationByModule,
    filteredNavigationByModulesForNestedLinks,
} from "../Repository/navigation";

import styles from "./RawData.module.sass";

import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    downloadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    setDataFileStatusAction,
    deleteFilePermanentlyAction,
    deleteInvalidFilePermanentlyAction,
    hideFileAction,
    downloadFileAction,
    reprocessRepositoryAction,
    genericSearchAction,
    getFindingDetails,
    reprocessFileAction,
    getRawTablesAvailable,
    updateRowsAction,
    downloadCsvStreamAction
} from "../../store/actions";
import I18n from "../../I18n";
import useUpdateThemes from "../../hooks/useUpdateThemes";

import proxy from "../../routes/proxy";
import {
    filteredQueriesByModules,
    filteredQueriesByModulesForMenu,
    getFirstModuleOfArray,
} from "../../routes/manageRouting";

import RepositoryStatus from "../../components/RepositoryStatus/RepositoryStatus";

import { mapPropertiesForMenu } from "../../utils";
import {
    decodeUserSession,
    redirectNotValid,
    buildStaticLinks,
    BuildSovosNavProps,
} from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import { urlRedirectLink } from "../../utils/routingUtils";
import GlobalLayout from "../../components/GlobalLayout/GlobalLayout";
import {
    SovosBox,
    SovosCircularProgress,
    SovosSnackbar,
    SovosPageLayout,
    SovosPageContentGeneric,
} from "mosaic-react";
import { useAuth } from "react-oidc-context";
import HeaderEditMode from "./Header/HeaderEditMode";
import {
    mapRawDataByLine,
    onCellHighlightDataGrid,
    onRowUpdateError,
    updateRawDataHeaderArray,
} from "./utils";

const RawData = (props) => {
    const {
        modules,
        user,
        fetch,
        match,
        route,
        location,
        history,
        reload,
        getRawTablesAvailable,
        updateRowsAction
    } = props;
    const { params: matchParams } = match || {};
    let { fiscalYear, importId } = matchParams;
    const { projectId, modulePrefix, locale = "en", entityRawData } = matchParams;
    const { results: fetchResults, status: fetchStatus } = fetch || {};
    const [editMode, setEditMode] = useState(false);
    // Remove viewMode from URL if present (RawData.js doesn't use new_navigation)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has("viewMode")) {
            searchParams.delete("viewMode");
            const newSearch = searchParams.toString();
            const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
            history.replace(newUrl);
        }
    }, [location.search, location.pathname, history]);

    let rowsEdited = {};
    let listResults = [];
    let rawDataEdit = false;

    const auth = useAuth();

    const userInformation = decodeUserSession(user);

    const i18n = I18n.use(locale);

    const defaultNotification = { open: false, message: "", type: "", onClose: () => { } };
    const [notification, pushNotification] = useState({ ...defaultNotification });

    const { menuOrganizationProject = [] } = mapProperties(
        {
            menuOrganizationProject: "query(0).list",
        },
        fetchResults,
        fetchStatus,
        RawData.queries(matchParams),
        props,
        matchParams,
        i18n,
        locale,
    );

    const { organizationProjects = {}, status } = mapProperties(
        {
            organizationProjects: "query(1)",
            status: "status(0)",
        },
        fetchResults,
        fetchStatus,
        RawData.queries(matchParams),
        props,
        matchParams,
        i18n,
        locale,
    );

    const { loading = true, success = false } = status || {};

    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    // Apply theme with priority logic: Project > Organization > Default
    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "RawData");
    }, [organizationProjects, projectId, updateThemes]);

    const { rawDataModules = [] } = mapProperties(
        {
            rawDataModules: "query(3).list",
        },
        fetchResults,
        fetchStatus,
        RawData.queries(matchParams),
        props,
        matchParams,
        i18n,
        locale,
    );

    const hasReset = useRef(false);
    useEffect(() => {
        const hasValidUrlModule = hasModule(rawDataModules, modulePrefix)
        if (!hasReset.current && !location.state?.resetDone && rawDataModules?.length && !hasValidUrlModule) {
            hasReset.current = true;
            const to = hydrateRoute(match.path, {
                ...matchParams,
                modulePrefix: rawDataModules[0],
                entityRawData: undefined,
            });
            history.replace(to, { resetDone: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawDataModules]);

    const getTotalExportLines = fetchResults[`${locale}_get_limit_export`] || "";

    const project = organizationProjects
        ? (organizationProjects.projects || []).find(({ id }) => id === projectId) || {
            name: "",
        }
        : { name: "" };

    const { fiscalYears: projectFiscalYears } = project || {};

    const fiscalYears = Object.keys(projectFiscalYears || []);

    if (!fiscalYear && project && projectFiscalYears) {
        fiscalYear = fiscalYears.length > 0 ? Math.max(...fiscalYears) : "";
    }

    const fiscalYearsWithImportIds = projectFiscalYears || {};
    const importIdsFromUrlFiscalYear = fiscalYearsWithImportIds[fiscalYear];

    if (!importId && importIdsFromUrlFiscalYear && importIdsFromUrlFiscalYear.length) {
        const [maxImportId] = importIdsFromUrlFiscalYear;
        importId = maxImportId?.id;
    }

    const repositoryStatus = fetchResults[
        `${locale}_repository_status_fiscalYear_${fiscalYear}_projectId_${projectId}`
    ] || { status: 1 };
    const { repoStatus = 0 } = repositoryStatus
    const queriesForMenu = filteredQueriesByModulesForMenu(modules);
    const navData = mapPropertiesForMenu(
        queriesForMenu,
        fetchResults,
        locale,
        fiscalYear,
        projectId,
        importId,
    );

    const firstModuleFromProject = getFirstModuleOfArray(modules);
    const moduleConfigs = project
        ? (project.modules || []).find(({ name }) => name === firstModuleFromProject)
        : undefined;
    const importIdToUse =
        moduleConfigs && moduleConfigs.navigation_by_import_id ? importId : "full";

    const params = {
        ...(parse(location.search) || {}),
        ...(matchParams || {}),
    };

    const onRowEditDatagrid = (updatedRowObject, originalRowObject) => {
        const {
            audit_logs: updatedAuditLogs,
            oldRow: updatedOldRow,
            uniqueId: updatedUniqueID,
            ...updatedRow
        } = updatedRowObject;
        const {
            audit_logs: originalAuditLogs,
            oldRow: originalOldRow,
            uniqueId: originalUniqueID,
            ...originalRow
        } = originalRowObject;
        for (const key in updatedRow) {
            if (updatedRow[key] !== originalRow[key]) {
                const updateObject = {
                    [updatedRow.id]: {
                        updated: {
                            [`${key}`]: updatedRow[key],
                            ...rowsEdited[updatedRow.id]?.updated,
                        },
                        original: rowsEdited[updatedRow.id]?.original
                            ? rowsEdited[updatedRow.id]?.original
                            : originalRow,
                    },
                };
                rowsEdited = { ...rowsEdited, ...updateObject };
            }
        }
        return { ...updatedRow, uniqueId: updatedUniqueID };
    };

    const routeQueries = (route.queries && route.queries(params)) || [];
    // Only create children (DataGrid) when entityRawData is present
    const children = entityRawData && route.components ? route.components.map((c, index) => {
        const Component = c.component;
        const componentParsedProps = mapProperties(
            c.props,
            fetchResults,
            fetchStatus,
            routeQueries,
            props,
            params,
            i18n,
            locale,
        );

        const header = updateRawDataHeaderArray(componentParsedProps);

        const customRawDataView =
            componentParsedProps?.items?.length && componentParsedProps?.header?.length
                ? mapRawDataByLine(
                    componentParsedProps.items,
                    componentParsedProps.header,
                    entityRawData,
                    projectId,
                    fiscalYear,
                    importId,
                    modulePrefix,
                )
                : [];
        const componentProps = {
            ...componentParsedProps,
            header: header,
            items: customRawDataView,
            modules,
            editMode: editMode,
        };
        if (componentProps && componentProps.items?.length && componentProps.cellHighlight) {
            componentProps.modifierssx = {
                "& .highlight": {
                    backgroundColor: "#E0EAF8",
                },
            };
            componentProps.onCellHighlight = (params) => onCellHighlightDataGrid(params);
        }
        if (componentProps && componentProps.items?.length && componentProps.onRowEdit) {
            componentProps.onProcessRowUpdateErrorAction = () =>
                onRowUpdateError(pushNotification, I18n);
            componentProps.onRowEdit = (updatedRow, originalRow) =>
                onRowEditDatagrid(updatedRow, originalRow);
        }

        rawDataEdit = componentParsedProps.rawDataEdit || false;
        listResults = componentProps.items || [];
        return (
            <Component
                key={index}
                modifiers={c.modifiers}
                {...componentProps}
                activity={fetch.activity}
            />
        );
    }) : [];

    const branch = matchRoutes([route, ...(route.routes || [])], location.pathname);
    //TODO: review logic
    const branchQueries = branch.reduce((allQueries, { route }) => {
        const innerQueries = route.queries ? route.queries(params) : [];
        allQueries = [...innerQueries];
        return allQueries;
    }, []);

    const downloadableQueries = branchQueries?.filter(({ downloadable }) => downloadable);

    const repositoryNavigation = filteredNavigationByModulesForNestedLinks(
        modules || [],
        asideRepositoryNavigationByModule,
    );

    const _navigation = (route.navigation || []).map((item) => {
        if (item?.name === "label(Repository)") {
            delete item.to;
            item.navigation = [...repositoryNavigation];
        }

        return item;
    });

    const { navigation } = mapProperties(
        { navigation: _navigation },
        fetchResults,
        fetchStatus,
        RawData.queries({ ...params, fiscalYear, importId }),
        {
            ...props,
            ...navData,
        },
        { ...params, fiscalYear, importId },
        i18n,
        locale,
    );

    const staticLinks = buildStaticLinks(
        navigation,
        history,
        { ...matchParams, fiscalYear, importId },
        i18n,
    );

    //TODO Pass this to an util and take into account fetch.activity
    const isPageLoading = (status, activity) => {
        return (
            activity ||
            activity === null ||
            Object.keys(status).some((queryKey) => {
                return status[queryKey].loading === true;
            })
        );
    };

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    if (redirect) {
        return redirect;
    }

    if (!projectId) {
        return urlRedirectLink(`${locale ? `/${locale}` : ""}/`);
    }

    if (!fiscalYear && !importId && !fiscalYears.length) {
        return urlRedirectLink(
            `${locale ? `/${locale}` : "/en"}/${projectId}/0000/full/repository/activity`,
        );
    }

    if (fiscalYear === "0000" && fiscalYears.length) {
        const to = hydrateRoute(match.path, {
            ...matchParams,
            fiscalYear: Math.max(...fiscalYears),
            importId: importIdToUse,
            firstModuleFromProject,
        });

        if (to !== match.url && !to.match(/\*$/)) {
            return urlRedirectLink(to);
        }
    }

    if (
        fiscalYear &&
        importIdToUse &&
        !isNaN(Number(matchParams.fiscalYear)) &&
        !isNaN(Number(matchParams.importId)) &&
        fiscalYear !== matchParams.fiscalYear &&
        importIdToUse !== matchParams.importId &&
        firstModuleFromProject
    ) {
        const to = hydrateRoute(match.path, {
            ...matchParams,
            fiscalYear,
            importId: importIdToUse,
            firstModuleFromProject,
        });

        if (to !== match.url && !to.match(/\*$/)) {
            return urlRedirectLink(to);
        }
    }

    // Only redirect to add modulePrefix when viewing a specific entity (not the landing page)
    if (rawDataModules?.length && !matchParams.modulePrefix) {
        const to = hydrateRoute(match.path, {
            ...matchParams,
            fiscalYear: Math.max(...fiscalYears),
            importId: importIdToUse,
            modulePrefix: rawDataModules?.length ? rawDataModules[0] : "",
        });
        return urlRedirectLink(to);
    }

    const organizationSelectedInMenu = (menuOrganizationProject || []).find(({ nestedAccounts }) =>
        (nestedAccounts || []).some(({ id }) => id === projectId),
    );

    const {
        accounts: fullOrganizationsWithProjects,
        selectedAccount: selectedOrganizationWithProjects,
    } = cleanPropertiesNestedInMenu(menuOrganizationProject, organizationSelectedInMenu);

    const navProps = BuildSovosNavProps(
        undefined,
        fullOrganizationsWithProjects,
        selectedOrganizationWithProjects,
        { locale, history, proxy, params: { ...params, fiscalYear, importId } },
        userInformation,
        staticLinks || [],
        projectId,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        repositoryStatus,
        getTotalExportLines,
        staticLinks,
        styles,
        organizationProjects
    };

    const saveRawData = (e) => {
        updateRowsAction(
            projectId,
            modulePrefix,
            fiscalYear,
            importId,
            entityRawData,
            JSON.stringify(rowsEdited),
            "update_rows_raw_data",
        )
            .then((res) => {
                if (!res?.update_rows_raw_data) {
                    onRowUpdateError(pushNotification, I18n);
                    return;
                }
                pushNotification({
                    open: true,
                    message: I18n.translate`Your edit have been saved successfully`,
                    type: "confirmation",
                    timeout: "short",
                });
                setEditMode(false);
                reload && reload();
            })
            .catch(() => {
                onRowUpdateError(pushNotification, I18n);
            });
    };

    const headerProps = {
        fiscalYears: fiscalYearsWithImportIds,
        importIds: importIdsFromUrlFiscalYear,
        rawDataModules: rawDataModules,
        getRawTablesAvailable: getRawTablesAvailable,
        downloadableQueries: downloadableQueries,
        title: i18n`Raw Data`,
        titleBarActions: [
            <HeaderEditMode
                key="edit-mode"
                editMode={editMode}
                setEditMode={setEditMode}
                onClickSave={saveRawData}
                params={params}
                list={listResults}
                rawDataEdit={rawDataEdit}
            />,
        ],
    };

    const { title, titleBarActions } = headerProps;
    return (
        LoadingComponent || (
            <GlobalLayout {...globalLayoutProps}>
                <SovosPageLayout
                    className={repoStatus ? `${styles["with-repository-banner"]}` : ""}
                    pageTitle={title || ""}
                    titleBarActions={titleBarActions}
                >
                    <RepositoryStatus modules={modules} />
                    <SovosPageContentGeneric sx={{ padding: "0 !important" }}>
                        <Connect to={["search", "downloading"]}>
                            {({ search, downloading }) => (
                                <>
                                    <Header
                                        rawDataModules={rawDataModules}
                                        getRawTablesAvailable={getRawTablesAvailable}
                                        fiscalYears={fiscalYearsWithImportIds}
                                        importIds={importIdsFromUrlFiscalYear}
                                        modules={modules}
                                    />
                                </>
                            )}
                        </Connect>
                        {isPageLoading(fetch?.status, fetch?.activity) ? (
                            <SovosBox
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <SovosCircularProgress aria-label={"Loading"} />
                            </SovosBox>
                        ) : (
                            <SovosBox
                                sx={{
                                    padding: "0 !important",
                                    marginLeft: "24px",
                                    marginRight: "24px",
                                }}
                            >
                                {children}
                                {renderRoutes(route.routes)}
                                <SovosSnackbar
                                    onClose={() => {
                                        pushNotification({
                                            ...notification,
                                            open: false,
                                        });
                                    }}
                                    message={notification && notification.message}
                                    open={notification && notification.open}
                                    variant={
                                        notification && notification.type
                                            ? notification.type
                                            : "confirmation"
                                    }
                                    timeout={
                                        notification && notification.timeout
                                            ? notification.timeout
                                            : "medium"
                                    }
                                    disableWindowBlurListener
                                />
                            </SovosBox>
                        )}
                    </SovosPageContentGeneric>
                </SovosPageLayout>
            </GlobalLayout>
        )
    );
};

RawData.queries = (args, userModulesByProject) => {
    const queries = filteredQueriesByModules(userModulesByProject);
    const deepCopyQueries = JSON.parse(JSON.stringify(queries));
    if (deepCopyQueries) {
        //needed to change arguments to the respective value
        deepCopyQueries.forEach((query) =>
            Object.keys(query.args).forEach((k) =>
                query.args[k] ? query.args[k] : (query.args[k] = args[k] || query.args[k]),
            ),
        );
    }

    const allQueries = [
        {
            resource: "menu_organizations_projects",
            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "organization_projects_by_projectId",
            args: {
                projectId: args.projectId,
            },
            body: "id,name,maxFiscalYear,fiscalYears,calendarYears,mosaicThemeConfig,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,metadata,mosaicThemeConfig,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive, maxFiscalYear",
            forceRefresh: true,
        },
        {
            resource: "get_limit_export",
        },
        {
            resource: "file_type_modules_raw_data",
            args: {
                projectId: args.projectId,
            },
        },
        ...deepCopyQueries,
    ];
    if (userModulesByProject && userModulesByProject.includes("merge_collisions")) {
        allQueries.push({
            resource: "repository_status",
            args: {
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
            },
            body: "repoStatus,fiscalYear,fileType,autoMerge",
        });
    }

    return allQueries;
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote, downloading }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        getFindingDetails,
        setDataFileStatus: setDataFileStatusAction,
        deleteFilePermanently: deleteFilePermanentlyAction,
        deleteInvalidFilePermanently: deleteInvalidFilePermanentlyAction,
        hideFile: hideFileAction,
        downloadFile: downloadFileAction,
        reprocessRepository: reprocessRepositoryAction,
        uploadFiles: uploadAction,
        download: downloadAction,
        submitAuditNote: auditNoteSubmit,
        genericSearchAction,
        reprocessFiles: reprocessFileAction,
        getRawTablesAvailable,
        updateRowsAction,
        downloadCsvStreamAction: downloadCsvStreamAction
    },
)(withRouter(RawData));
